import * as React from "react";
import { useState, useContext, useEffect, FunctionComponent } from "react";
import { useNavigate } from "react-router-dom";

// MUI
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";

// Custom Components
import NavDrawer from "@/ui/NavDrawer/NavDrawer";
import { menuHGSS, menuHPS, menuRegUser } from "@/routes/menu";
import UserContext from "@/components/UserContext/UserContext";
import Loader from "@/ui/Loader/Loader";
import AppHeader from "@/ui/Header/AppHeader";
import useTitle from '@/components/useTitle';

// Background Image
import Image from "@/images/bkg/hgss-background.jpg";

// Types
import { IAppPage } from "@/@types/ui/AppPage";
import { UserContextType } from "@/@types/context/UserContext";
import { OwnerOrganisations } from "@/lib/ownerOrganisations";

type SidebarSettingsType = {
  open: boolean;
  collapsed: string[];
};

const AppPage: FunctionComponent<IAppPage> = (props) => {
  const userContext = useContext(UserContext) as UserContextType;

  const { needPermission = [], showBackgroudPhoto, hideNavDrawer } = props;

  const sidebarSettings = getSidebarSetting();
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(sidebarSettings.open);
  const [collapsedNavLinks, setCollapsedNavLinks] = useState<string[]>(
    sidebarSettings.collapsed
  );

  useTitle();

  const navigate = useNavigate();

  const toggleSidebar = (): void => {
    setSidebarOpen((prevState) => !prevState);

    // eslint-disable-next-line @typescript-eslint/no-shadow
    const sidebarSettings = JSON.parse(localStorage.getItem('sidebar') as string);
    sidebarSettings.open = !sidebarOpen;

    localStorage.setItem('sidebar', JSON.stringify(sidebarSettings));
  };

  const handleNavbarLinkCollapse = (name: string): void => {
    const collapsedCopy = [...collapsedNavLinks];

    // add name to collapseedcopy or remove from it if it exists
    let found = false;
    for (let i = 0; i < collapsedCopy.length; i += 1) {
      if (collapsedCopy[i] === name) {
        found = true;
        collapsedCopy.splice(i, 1);
      }
    }

    if (!found) {
      collapsedCopy.push(name);
    }

    // eslint-disable-next-line @typescript-eslint/no-shadow
    const sidebarSettings = JSON.parse(localStorage.getItem('sidebar') as string);
    sidebarSettings.collapsed = collapsedCopy;

    localStorage.setItem('sidebar', JSON.stringify(sidebarSettings));

    setCollapsedNavLinks(collapsedCopy);
  };

  const forbidden: boolean =
    needPermission.length > 0 && !userContext.hasAnyPermission(needPermission);

  useEffect(() => {
    if (userContext && userContext.loaded && forbidden) {
      console.warn("Tried to access forbidden page, Redirecting...");
      navigate("/");
    }
  }, [userContext.loaded, forbidden]);
  
  const getMenu = (orgId: number) => {
    switch (orgId) {
      case OwnerOrganisations.HGSS.code:
        return menuHGSS;
      case OwnerOrganisations.HPS.code:
        return menuHPS;
      default:
        return menuRegUser;
    }
  }

  if (sidebarOpen && hideNavDrawer) {
    toggleSidebar();
  }

  const menu = getMenu(userContext.organisationId);

  return userContext && userContext.loaded && !forbidden ? (
    <div id="page">
      <AppHeader
        menu={menu}
        sidebarOpen={sidebarOpen}
        toggleSidebar={toggleSidebar}
        hideNavDrawer={hideNavDrawer}
      />
      <Box m={0} sx={{ display: { xs: "none", md: "block" } }}>
        {!hideNavDrawer && (
          <NavDrawer
            menu={menu}
            open={sidebarOpen}
            toggleOpen={toggleSidebar}
            collapsedNavLinks={collapsedNavLinks}
            onNavLinkCollapse={handleNavbarLinkCollapse}
          />
        )}
      </Box>
      <main id="content-wrapper" style={{ flexGrow: 1, width: sidebarOpen ? "calc(100% - 240px)" : "100%" }}>
        <Toolbar variant="dense" />
        <div
          id="content"
          style={
            showBackgroudPhoto
              ? {
                  backgroundImage: `url(${Image})`,
                  objectFit: "contain",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center",
                  backgroundSize: "cover",
                }
              : undefined
          }
        >
          {// eslint-disable-next-line react/destructuring-assignment
          }{props.children}
        </div>
      </main>
    </div>
  ) : (
    <Loader open />
  );
};

const getSidebarSetting = (): SidebarSettingsType => {
  // @ts-ignore
  let sidebarSettings;
  try {
    sidebarSettings = JSON.parse(localStorage.getItem('sidebar') as string);

    if (!isSidebarSettingsType(sidebarSettings)) {
      sidebarSettings = { open: false, collapsed: [] };
      localStorage.setItem('sidebar', JSON.stringify(sidebarSettings));
    }

    return sidebarSettings as SidebarSettingsType;
  } catch {
    sidebarSettings = { open: false, collapsed: [] };
    localStorage.setItem('sidebar', JSON.stringify(sidebarSettings));

    return sidebarSettings as SidebarSettingsType;
  }
};

const isSidebarSettingsType = (obj: any): obj is SidebarSettingsType => (
  typeof obj === 'object' &&
  obj !== null &&
  'open' in obj &&
  typeof obj.open === 'boolean' &&
  'collapsed' in obj &&
  Array.isArray(obj.collapsed)
);

export default AppPage;
